import React, { createContext, useMemo } from 'react';
import proptypes from 'prop-types';
import MESSAGES from '../config/messages';
import { usePromoModel } from '../hooks/usePromoModel';
import { formatPrice, formattedReward, toWord } from '../utils/utils';
import {
  BACKWARDS_BOGO_SUBEXPERIENCE_TAGS,
  DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS,
  FORWARDS_BOGO_SUBEXPERIENCE_TAGS,
  MSB_SUBEXPERIENCE_TAGS
} from '../utils/constants';

const { node } = proptypes;

/*
 * MESSAGES is a map of strings that the messages within the drawer can pull from. Dynamic values can be inserted
 * by writing in the format `$varName`.
 *
 * Current list of known variables:
 *
 * - qualifyingAmount
 * - rewardAmount
 * - src1ListCount
 * - tgt1ListCount
 *
 * Escape characters can also be used to apply certain styling:
 * - `\n` new line
 * - `\u00a0` non breaking space
 */

const DEFAULT_PROMO_MESSAGE_CONTEXT = Object.freeze({ messages: MESSAGES });

export const PromoMessageContext = createContext(DEFAULT_PROMO_MESSAGE_CONTEXT);

export const PromoMessageProvider = ({ children }) => {
  const {
    rewardTiers,
    sourceEligibilityCriteria,
    targetEligibilityCriteria,
    subExperience
  } = usePromoModel();

  // The names of the keys matter for this object. The message format function uses the key names
  // to determine where in the string it needs to replace. Be careful if you change any property names here.
  const messageReplaceValues = useMemo(() => {
    let qualifyingAmount = null;
    let rewardAmount = null;
    let analyticsRewardAmount = null;
    let analyticsQualifyingAmount = null;

    const {
      minThresholdVal,
      rewardVal,
      rewardType,
    } = rewardTiers?.[0] || {};

    switch (subExperience) {
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYPercentageOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyXGetOnePercentageOff:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetPercentageOff: {
      rewardAmount = `${rewardVal}%`;
      analyticsRewardAmount = rewardVal;
      break;
    }
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetYDollarOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyXGetOneDollarOff:
    case FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyThePairAndSave:
    case BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetDollarOff: {
      rewardAmount = formattedReward({ rewardVal }, true, rewardType);
      analyticsRewardAmount = rewardVal;
      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetOne: {
      qualifyingAmount = formatPrice(sourceEligibilityCriteria, true);
      analyticsQualifyingAmount = sourceEligibilityCriteria;
      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetPercentageOff: {
      qualifyingAmount = formatPrice(sourceEligibilityCriteria, true);
      analyticsQualifyingAmount = sourceEligibilityCriteria;
      rewardAmount = `${rewardVal}%`;
      analyticsRewardAmount = rewardVal;
      break;
    }
    case DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCE_TAGS.buyMinAmountGetDollarOff: {
      qualifyingAmount = formatPrice(sourceEligibilityCriteria, true);
      analyticsQualifyingAmount = sourceEligibilityCriteria;
      rewardAmount = formattedReward({ rewardVal }, true, rewardType);
      analyticsRewardAmount = rewardVal;
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB1: {
      qualifyingAmount = minThresholdVal;
      analyticsQualifyingAmount = minThresholdVal;
      rewardAmount = `${rewardVal}%`;
      analyticsRewardAmount = rewardVal;
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB2:
    case MSB_SUBEXPERIENCE_TAGS.MSB3: {
      qualifyingAmount = minThresholdVal;
      analyticsQualifyingAmount = minThresholdVal;
      rewardAmount = formatPrice(rewardVal, true);
      analyticsRewardAmount = rewardVal;
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB4: {
      qualifyingAmount = formatPrice(minThresholdVal, true);
      analyticsQualifyingAmount = minThresholdVal;
      rewardAmount = `${rewardVal}%`;
      analyticsRewardAmount = rewardVal;
      break;
    }
    case MSB_SUBEXPERIENCE_TAGS.MSB5:
    case MSB_SUBEXPERIENCE_TAGS.MSB6: {
      qualifyingAmount = formatPrice(minThresholdVal, true);
      analyticsQualifyingAmount = minThresholdVal;
      rewardAmount = formatPrice(rewardVal, true);
      analyticsRewardAmount = rewardVal;
      break;
    }
    default: {
      break;
    }
    }

    return Object.freeze({
      qualifyingAmount,
      rewardAmount,
      analyticsRewardAmount,
      analyticsQualifyingAmount,
      src1MinPurchaseQuantity: sourceEligibilityCriteria || null,
      src1MinPurchaseQuantityWord: toWord(sourceEligibilityCriteria) || null,
      tgt1MinPurchaseQuantity: targetEligibilityCriteria || null,
      tgt1MinPurchaseQuantityWord: toWord(targetEligibilityCriteria) || null
    });
  }, [
    rewardTiers,
    subExperience,
    sourceEligibilityCriteria,
    targetEligibilityCriteria
  ]);

  const contextValue = useMemo(() => {
    return Object.freeze({
      messageReplaceValues,
      messages: MESSAGES
    });
  }, [messageReplaceValues]);

  return (
    <PromoMessageContext.Provider value={contextValue}>
      {children}
    </PromoMessageContext.Provider>
  );
};

PromoMessageProvider.propTypes = {
  children: node.isRequired
};
